import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid2, TextField, Typography } from '@mui/material';
import { SelectField } from 'components';
import { VTKDataRefreshEmailAction } from '../actions/DataManagementActions';
import { enqueueSnackbar } from 'actions';
import ConfirmDialog from './ConfirmDialog';

// Moved outside component to avoid recreation on every render
const PERIOD_OPTIONS = [
	{ value: 'invalid', disabled: true, text: 'Select one option' },
	{ value: 'Morning', disabled: false, text: 'Morning' },
	{ value: 'AfternoonEvening', disabled: false, text: 'Afternoon / Evening' },
];

const PERIOD_LABELS = {
	Morning: 'Morning',
	AfternoonEvening: 'Afternoon / Evening',
};

const getToday = () => new Date().toISOString().split('T')[0];

// locked: true while another VTK import is running (shared semaphore) - disables submission.
// onAfterSubmit: optional callback invoked once the request settles, so a parent hub can refresh the lock status.
const VTKDataRefreshEmailForm = ({ locked = false, onAfterSubmit } = {}) => {
	const userId = useSelector(state => state.session.user.id);
	const dispatch = useDispatch();

	const [date, setDate] = useState(getToday());
	const [period, setPeriod] = useState('invalid');
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const canSubmit = Boolean(date) && period !== 'invalid';

	const handleConfirm = async () => {
		const formData = new FormData();
		formData.append('UserId', userId);
		formData.append('Date', date);
		formData.append('Period', period);

		try {
			setIsLoading(true);
			const data = await VTKDataRefreshEmailAction(formData);
			dispatch(enqueueSnackbar({ message: data.message, options: { variant: 'success' } }));
		} catch (error) {
			dispatch(enqueueSnackbar({ message: `The process has not started because an error occured. ${error}`, options: { variant: 'error' } }));
		} finally {
			setIsLoading(false);
			setConfirmOpen(false);
			if (onAfterSubmit) onAfterSubmit();
		}
	};

	return (
		<Grid2 container spacing={2}>
			<Grid2 item size={12}>
				<Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
					Select the date and the part of the day you want to import the e-mails from.
				</Typography>
			</Grid2>

			{locked && (
				<Grid2 item size={12}>
					<Typography variant="body2" align="center" color="error">
						An import is currently running. Please wait for it to finish before starting a new one.
					</Typography>
				</Grid2>
			)}

			<Grid2 item size={12}>
				<TextField
					id="inputDate"
					name="inputDate"
					label="Date"
					type="date"
					variant="outlined"
					fullWidth
					value={date}
					onChange={(e) => setDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					disabled={isLoading || locked}
				/>
			</Grid2>

			<Grid2 item size={12}>
				<SelectField
					id="inputPeriod"
					name="inputPeriod"
					value={period}
					onChange={(e) => setPeriod(e.target.value)}
					hintText="Part of the Day"
					menuItems={PERIOD_OPTIONS}
					disabled={isLoading || locked}
				/>
			</Grid2>

			<Grid2 item size={12}>
				<Button
					variant="contained"
					color="primary"
					fullWidth
					onClick={() => setConfirmOpen(true)}
					disabled={isLoading || locked || !canSubmit}
				>
					Update Data
				</Button>
			</Grid2>

			<ConfirmDialog
				open={confirmOpen}
				title="Update Data"
				message={`Are you sure you want to update data for ${date} (${PERIOD_LABELS[period] || ''})?`}
				onConfirm={handleConfirm}
				onCancel={() => setConfirmOpen(false)}
				isLoading={isLoading}
			/>
		</Grid2>
	);
};

export default VTKDataRefreshEmailForm;
